import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, Plane, Search } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from './LanguageSelector';
import SearchModal from './SearchModal';
import { vehicleCategoriesAPI } from '../services/api';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isVehicleDropdownOpen, setIsVehicleDropdownOpen] = useState(false);
  const [isMobileVehicleOpen, setIsMobileVehicleOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await vehicleCategoriesAPI.getAll();
        setCategories(data || []);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
        // Fallback categories
        setCategories([
          { id: 'all', name: 'All Vehicles', name_tr: 'Tüm Araçlar' },
          { id: 'iha-s1', name: 'UAV-S1', name_tr: 'İHA-S1' },
          { id: 'iha-s2', name: 'UAV-S2', name_tr: 'İHA-S2' },
          { id: 'prototype', name: 'Prototype', name_tr: 'Prototip' }
        ]);
      }
    };
    fetchCategories();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsVehicleDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Global Ctrl+K / Cmd+K shortcut
  useEffect(() => {
    const handleShortcut = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, []);

  const openSearch = () => {
    setIsMenuOpen(false);
    setIsSearchOpen(true);
  };

  // Get localized category name
  const getCategoryName = (cat) => {
    if (language === 'tr' && cat.name_tr) return cat.name_tr;
    return cat.name;
  };

  // Navigation links (without aircraft - we'll handle it separately)
  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.team'), path: '/team' },
    { name: t('nav.buildLogs'), path: '/build-logs' },
    { name: t('nav.achievements'), path: '/achievements' },
    { name: t('nav.sponsors'), path: '/sponsors' },
    { name: t('nav.contact'), path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;
  const isAircraftActive = location.pathname === '/aircraft' || location.pathname.startsWith('/vehicle') || location.pathname.startsWith('/vehicles');

  // Handle category click - navigate to dedicated category page
  const handleCategoryClick = (categoryId) => {
    setIsVehicleDropdownOpen(false);
    setIsMenuOpen(false);
    setIsMobileVehicleOpen(false);
    if (categoryId === 'all') {
      navigate('/aircraft');
    } else {
      navigate(`/vehicles/${categoryId}`);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 h-24" style={{
      background: 'rgba(15, 23, 42, 0.8)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      borderBottom: '1px solid rgba(0, 240, 255, 0.2)',
      boxShadow: '0 4px 24px rgba(0, 0, 0, 0.2)'
    }}>
      <div className="max-w-[1400px] mx-auto px-[7.6923%] h-full flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_aero-eagles/artifacts/2k3o5u10_KARTAL%20A%C4%B0R%20LOGO.png" 
            alt="Kartal Air" 
            className="h-20 object-contain cursor-pointer"
            style={{
              filter: 'drop-shadow(0 0 10px rgba(0, 240, 255, 0.5))'
            }}
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-8">
          {/* Home Link */}
          <Link
            to="/"
            className={`text-base font-normal transition-all duration-300 ${
              isActive('/')
                ? 'text-[#00F0FF]'
                : 'text-[#94A3B8] hover:text-[#F8FAFC]'
            }`}
            style={isActive('/') ? {
              textShadow: '0 0 10px rgba(0, 240, 255, 0.8), 0 0 20px rgba(0, 240, 255, 0.4)'
            } : {}}
          >
            {t('nav.home')}
          </Link>

          {/* Aircraft Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsVehicleDropdownOpen(!isVehicleDropdownOpen)}
              className={`flex items-center gap-1 text-base font-normal transition-all duration-300 ${
                isAircraftActive
                  ? 'text-[#00F0FF]'
                  : 'text-[#94A3B8] hover:text-[#F8FAFC]'
              }`}
              style={isAircraftActive ? {
                textShadow: '0 0 10px rgba(0, 240, 255, 0.8), 0 0 20px rgba(0, 240, 255, 0.4)'
              } : {}}
              data-testid="vehicles-dropdown-btn"
            >
              {t('nav.aircraft')}
              <ChevronDown 
                size={16} 
                className={`transition-transform duration-200 ${isVehicleDropdownOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {/* Dropdown Menu */}
            {isVehicleDropdownOpen && (
              <div 
                className="absolute top-full left-0 mt-2 py-2 min-w-[200px] rounded-lg shadow-2xl"
                style={{
                  background: 'rgba(15, 23, 42, 0.95)',
                  backdropFilter: 'blur(16px)',
                  border: '1px solid rgba(0, 240, 255, 0.2)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
                }}
                data-testid="vehicles-dropdown-menu"
              >
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryClick(cat.id)}
                    className="w-full px-4 py-3 text-left text-sm transition-all duration-200 flex items-center gap-3 hover:bg-[rgba(0,240,255,0.1)]"
                    style={{ color: '#F8FAFC' }}
                    data-testid={`dropdown-category-${cat.id}`}
                  >
                    <Plane size={16} style={{ color: '#00F0FF' }} />
                    <span>{getCategoryName(cat)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Other Navigation Links */}
          {navLinks.slice(1).map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-base font-normal transition-all duration-300 ${
                isActive(link.path)
                  ? 'text-[#00F0FF]'
                  : 'text-[#94A3B8] hover:text-[#F8FAFC]'
              }`}
              style={isActive(link.path) ? {
                textShadow: '0 0 10px rgba(0, 240, 255, 0.8), 0 0 20px rgba(0, 240, 255, 0.4)'
              } : {}}
            >
              {link.name}
            </Link>
          ))}
          
          {/* Search Trigger - Desktop */}
          <button
            onClick={openSearch}
            className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-300 hover:scale-105"
            style={{
              color: '#94A3B8',
              border: '1px solid rgba(0, 240, 255, 0.25)',
              background: 'rgba(0, 240, 255, 0.05)',
            }}
            onMouseOver={(e) => { e.currentTarget.style.color = '#00F0FF'; e.currentTarget.style.borderColor = 'rgba(0, 240, 255, 0.5)'; }}
            onMouseOut={(e) => { e.currentTarget.style.color = '#94A3B8'; e.currentTarget.style.borderColor = 'rgba(0, 240, 255, 0.25)'; }}
            aria-label={language === 'tr' ? 'Sitede ara' : 'Search site'}
            data-testid="desktop-search-trigger"
          >
            <Search size={18} />
            <span className="text-xs font-mono px-1.5 py-0.5 rounded" style={{
              border: '1px solid rgba(148, 163, 184, 0.3)',
              color: '#64748B',
            }}>
              {navigator.platform?.toUpperCase().includes('MAC') ? '⌘K' : 'Ctrl+K'}
            </span>
          </button>

          {/* Language Selector - Desktop */}
          <LanguageSelector />
        </nav>

        {/* Mobile Menu Button */}
        <div className="lg:hidden flex items-center gap-4">
          <LanguageSelector />
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="text-white p-2"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div style={{
          background: 'rgba(15, 23, 42, 0.95)',
          backdropFilter: 'blur(16px)',
          borderTop: '1px solid rgba(0, 240, 255, 0.2)'
        }}>
          <nav className="flex flex-col py-4">
            {/* Mobile Search Trigger Bar */}
            <div className="px-[7.6923%] pb-3">
              <button
                onClick={openSearch}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all duration-300"
                style={{
                  color: '#94A3B8',
                  border: '1px solid rgba(0, 240, 255, 0.3)',
                  background: 'rgba(0, 240, 255, 0.06)',
                }}
                aria-label={language === 'tr' ? 'Sitede ara' : 'Search site'}
                data-testid="mobile-search-trigger"
              >
                <Search size={20} style={{ color: '#00F0FF' }} />
                <span className="text-base">{language === 'tr' ? 'Sitede ara...' : 'Search site...'}</span>
              </button>
            </div>

            {/* Home */}
            <Link
              to="/"
              onClick={() => setIsMenuOpen(false)}
              className={`px-[7.6923%] py-4 text-lg transition-all duration-300 ${
                isActive('/')
                  ? 'text-[#00F0FF] bg-[rgba(0,240,255,0.1)]'
                  : 'text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[rgba(255,255,255,0.05)]'
              }`}
              style={isActive('/') ? {
                boxShadow: 'inset 4px 0 0 #00F0FF'
              } : {}}
            >
              {t('nav.home')}
            </Link>

            {/* Mobile Aircraft Dropdown */}
            <div>
              <button
                onClick={() => setIsMobileVehicleOpen(!isMobileVehicleOpen)}
                className={`w-full px-[7.6923%] py-4 text-lg transition-all duration-300 flex items-center justify-between ${
                  isAircraftActive
                    ? 'text-[#00F0FF] bg-[rgba(0,240,255,0.1)]'
                    : 'text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[rgba(255,255,255,0.05)]'
                }`}
                style={isAircraftActive ? {
                  boxShadow: 'inset 4px 0 0 #00F0FF'
                } : {}}
              >
                {t('nav.aircraft')}
                <ChevronDown 
                  size={20} 
                  className={`transition-transform duration-200 ${isMobileVehicleOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {/* Mobile Category List */}
              {isMobileVehicleOpen && (
                <div 
                  className="py-2"
                  style={{ background: 'rgba(0, 0, 0, 0.2)' }}
                >
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryClick(cat.id)}
                      className="w-full px-[7.6923%] pl-[12%] py-3 text-base text-left transition-all duration-200 flex items-center gap-3"
                      style={{ color: '#94A3B8' }}
                    >
                      <Plane size={16} style={{ color: '#00F0FF' }} />
                      <span>{getCategoryName(cat)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Other Mobile Links */}
            {navLinks.slice(1).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className={`px-[7.6923%] py-4 text-lg transition-all duration-300 ${
                  isActive(link.path)
                    ? 'text-[#00F0FF] bg-[rgba(0,240,255,0.1)]'
                    : 'text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[rgba(255,255,255,0.05)]'
                }`}
                style={isActive(link.path) ? {
                  boxShadow: 'inset 4px 0 0 #00F0FF'
                } : {}}
              >
                {link.name}
              </Link>
            ))}
          </nav>
        </div>
      )}

      {/* Global Search Modal */}
      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </header>
  );
};

export default Header;
